import { motion } from 'framer-motion';
import Image from 'next/image';
import React, { FC, useContext } from 'react';
import getTailwindConfig from '../../../helpers/get-tailwind-config';
import useNextImageLoader from '../../../hooks/use-next-image-loader/use-next-image-loader';
import {
  DataLayerContext,
  DataLayerContextType,
} from '../../providers/data-layer/data-layer.provider';
import GtmEventNameEnum from '../../../models/gtm-event-name-enum';
import { PromoPanelSingleType } from '../../../models/promo-panel-single-type';
import ButtonPrimary from '../../atoms/button-primary/button-primary';

interface Props extends PromoPanelSingleType {
  readonly isActive: boolean;
}

const PromoPanelSingle: FC<Props> = ({
  description,
  heading,
  image,
  imageAltText,
  isActive,
  link,
  panelColour,
}: Props) => {
  const { pushToDataLayer } =
    useContext<DataLayerContextType>(DataLayerContext);
  const backgroundColor =
    panelColour === 'light-blue' ? 'bg-light-blue' : 'bg-grey-50';

  const fullConfig = getTailwindConfig();
  const lgWidth = parseInt(fullConfig.theme.screens.lg.replace('px', ''), 10);

  const variants = (isImage: boolean) => ({
    isActive: { opacity: 1, x: 0 },
    notActive: {
      opacity: 0,
      x:
        typeof window !== 'undefined' && window.innerWidth < lgWidth
          ? 0
          : `${isImage ? '' : '-'}40px`,
      y:
        typeof window !== 'undefined' && window.innerWidth < lgWidth
          ? `${isImage ? '-' : ''}20px`
          : 0,
    },
  });

  const transition = {
    damping: 40,
    delay: 0.35,
    duration: 0.6,
    mass: 3,
    stiffness: 300,
    type: 'spring',
  };

  return (
    <div className="h-full relative pt-16 lg:pt-24">
      <div
        className={
          `${backgroundColor} ` +
          'flex flex-col-reverse lg:flex-row h-full px-8 pt-16 pb-20 lg:py-16 lg:pl-20 lg:pr-0 xl:pl-24 rounded-xl'
        }
      >
        <motion.div
          animate={isActive ? 'isActive' : 'notActive'}
          className="w-full lg:w-5/12 xl:w-4/12"
          transition={transition}
          variants={variants(false)}
        >
          <h4 className="page-heading-four mb-5">{heading}</h4>

          <p className="text-grey-600 mb-6">{description}</p>

          <ButtonPrimary
            {...link}
            onClick={() => {
              pushToDataLayer(GtmEventNameEnum.ContactDirections, {
                cardTitle: heading,
              });
            }}
          />
        </motion.div>

        <motion.div
          animate={isActive ? 'isActive' : 'notActive'}
          className="flex h-72 lg:h-auto justify-center lg:justify-end -mt-32 lg:-my-40 relative w-full lg:w-7/12 xl:w-8/12"
          transition={transition}
          variants={variants(true)}
        >
          {image && (
            <Image
              layout="fill"
              loader={useNextImageLoader}
              objectFit="contain"
              quality={100}
              src={image}
              alt={imageAltText || 'Promo image'}
            />
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default PromoPanelSingle;
